import React, { useState, useEffect } from "react";
import CloseSharp from "@mui/icons-material/CloseSharp";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import PreviewSkeleton from "@/modules/crm/leads/components/quick-preview/preview-skeleton";
import { Typography } from "@mui/material";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import GetIcon, { GetFontAwesomeIcon } from "@/components/font-awesome-icon";
import { faCircleCheck, faPlus } from "@fortawesome/free-solid-svg-icons";
import ColorPickerPopOver from "./color-picker-popover";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { useDispatch } from "react-redux";
import { setGeneralState } from "@/store/slices/general/generalSlice";
import useCRMTheme from "@/hooks/useCRMTheme";
import useUserSettings, { SettingsKey } from "@/hooks/useUserSettings";
import CloseConfirmationModal from "./confirmation-modal";
import UIButton from "@/components/ui-controls/button";

const Modes = {
  Default: "default_theme",
  Light: "light_theme",
  Night: "night_theme",
};
const PrimaryDefautlColors = [
  { name: "primary_1", color: "var(--palette-primary_main)" },
  // { name: "primary_2", color: "#FF6737" },
  // { name: "primary_3", color: "#CCCAD1" },
];
const SecondaryDefautlColors = [
  { name: "primary_1", color: "var(--palette-secondary_main)" },
  // { name: "primary_2", color: "#FF6737" },
  // { name: "primary_3", color: "#CCCAD1" },
];

const themeKey = SettingsKey.user_theme;
const ThemeSidebar = ({ open = false, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState(Modes.Default);
  const [savedTheme, setSavedTheme] = useState(null);
  const [selectedPrimary, setSelectedPrimary] = useState("primary_1");
  const [selectedSecondary, setSelectedSecondary] = useState("primary_1");
  const [customPrimary, setCustomPrimary] = useState({});
  const [customSecondary, setCustomSecondary] = useState({});
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const { themeInfo, changeThemeColor } = useCRMTheme();
  const { addUserSettings, requestPending } = useUserSettings();
  const dispatch = useDispatch();

  const handleModes = (value) => {
    setMode(value);
    dispatch(setGeneralState({ theme: { ...themeInfo, mode: value } }));
  };

  const colorPickerHandler = (key, value) => {
    console.log("value", key, value);
    let colorInfo = { ...themeInfo, [key]: value };
    dispatch(setGeneralState({ theme: colorInfo }));
    changeThemeColor(colorInfo);
  };

  useEffect(() => {
    if (themeInfo) {
      setMode(themeInfo?.mode);
      setSelectedPrimary(themeInfo?.selectedPrimary || "primary_1");
      setSelectedSecondary(themeInfo?.selectedSecondary || "primary_1");
      setCustomPrimary(themeInfo?.customPrimary);
      setCustomSecondary(themeInfo?.customSecondary);
    }
  }, [themeInfo]);

  useEffect(() => {
    // set saved theme info in case of discard changes
    if (themeInfo) {
      setSavedTheme(themeInfo);
    }
  }, []);

  useEffect(() => {
    if (!requestPending) {
      onClose();
    }
  }, [requestPending]);

  const onApply = () => {
    let payload = {
      mode,
      selectedPrimary,
      selectedSecondary,
      customPrimary,
      customSecondary,
    };
    console.log("payload", payload);
    addUserSettings(themeKey, payload);
  };

  const onCloseSidebar = () => {
    let newChanges = JSON.stringify({
      mode,
      selectedPrimary,
      selectedSecondary,
      customPrimary,
      customSecondary,
    });
    if (JSON.stringify(savedTheme) !== newChanges) {
      setOpenConfirmModal(true);
    } else {
      onClose();
    }
  };

  const handleClose = () => {
    setOpenConfirmModal(false);
    // onClose();
  };

  const onUndoChanges = () => {
    // undo current changes set to prevoius selected theme
    if (savedTheme) {
      setMode(savedTheme?.mode);
      dispatch(setGeneralState({ theme: savedTheme }));
      changeThemeColor(savedTheme);
    }
    onClose();
    handleClose();
  };

  return (
    <Drawer
      anchor={"right"}
      open={open}
      onClose={onCloseSidebar}
      style={{ border: 0, borderRadius: 0 }}
      BackdropProps={{ invisible: true }}
    >
      {isLoading ? (
        <PreviewSkeleton onClose={onClose} />
      ) : (
        <Box
          sx={{
            display: "flex",
            width: 350,
            justifyContent: "flex-start",
            flexDirection: "column",
            minHeight: "calc(100vh - 2px)",
          }}
        >
          <Box
            className="d-flex align-items-center justify-content-space-between"
            sx={{
              px: 2,
              pt: 1.5,
              p: 0.5,
              backgroundColor: "var(--palette-header_bg)",
            }}
            role="presentation"
          >
            <Typography variant="h3" color={"custom.header_text"}>
              Theme
            </Typography>
            <IconButton
              onClick={() => onCloseSidebar()}
              sx={{ color: "var(--palette-header_text)" }}
              //   style={{ position: "absolute", right: "10px", top: "10px" }}
            >
              <CloseSharp />
            </IconButton>
          </Box>
          <Box
            sx={{
              px: 3,
              py: 2,
              mb: 5,
              overflowY: "auto",
              height: "calc(100vh - 112px)",
            }}
            className="custom-scroll"
          >
            <Typography variant="h4" sx={{ mb: 1, fontSize: "18px" }}>
              Select theme
            </Typography>
            <Box className="d-grid" sx={{ gap: 1 }}>
              <ModeSelect
                title={"Default"}
                selected={mode}
                mode={Modes.Default}
                handleModes={handleModes}
              />
              <ModeSelect
                title={"Light"}
                selected={mode}
                mode={Modes.Light}
                handleModes={handleModes}
                imgSrc="/assets/app-images/theme/light_theme.png"
              />
              <ModeSelect
                title={"Night"}
                selected={mode}
                mode={Modes.Night}
                handleModes={handleModes}
                imgSrc="/assets/app-images/theme/night_theme.png"
              />
            </Box>
            {/* <Box>
              <Typography variant="h5" sx={{ my: 1, fontSize: "16px" }}>
                Primary Color
              </Typography>
              <Box>
                <ColorSelector
                  key={1}
                  selected={selectedPrimary}
                  colors={PrimaryDefautlColors}
                  handleClick={(v) => colorPickerHandler("selectedPrimary", v)}
                  customColor={customPrimary}
                  setCustomColor={(v) => colorPickerHandler("customPrimary", v)}
                  selectedColor={"var(--palette-primary_button_text)"}
                />
              </Box>
            </Box> */}

            {/* <Box>
              <Typography variant="h5" sx={{ my: 1, fontSize: "16px" }}>
                Secondary Color
              </Typography>

              <ColorSelector
                key={2}
                selected={selectedSecondary}
                colors={SecondaryDefautlColors}
                handleClick={(v) => colorPickerHandler("selectedSecondary", v)}
                customColor={customSecondary}
                setCustomColor={(v) => colorPickerHandler("customSecondary", v)}
                selectedColor={"var(--palette-secondary_button_text)"}
              />
            </Box> */}
          </Box>

          <Grid container style={{ position: "absolute", bottom: 0 }}>
            <UIButton
              fullWidth
              variant="contained"
              title={<GetIcon title={"Apply"} />}
              onClick={onApply}
              disabled={requestPending}
              loading={requestPending}
              sx={{
                paddingTop: "15px",
                paddingBottom: "15px",
                borderRadius: 0,
              }}
            />
          </Grid>
        </Box>
      )}

      <CloseConfirmationModal
        open={openConfirmModal}
        onClose={handleClose}
        onConfirmCancel={onUndoChanges}
      />
    </Drawer>
  );
};

export default ThemeSidebar;

const ModeSelect = ({
  title,
  imgSrc = "/assets/app-images/theme/default_theme.png",
  selected,
  mode,
  handleModes,
}) => {
  return (
    <Box onClick={() => handleModes(mode)} sx={{ cursor: "pointer" }}>
      <GetIcon
        title={title}
        iconStyles={{
          marginRight: "10px",
          fontSize: "1.1rem",
          color: "var(--palette-secondary_button_text)",
        }}
        icon={selected == mode ? faCircleCheck : faCircle}
      />
      <img
        src={imgSrc}
        alt="success"
        style={{ width: "100%", height: "auto", marginTop: "10px" }}
      />
    </Box>
  );
};

const ColorSelector = ({
  selected,
  colors,
  handleClick = () => {},
  customColor = {},
  setCustomColor,
  selectedColor,
}) => {
  const handleColorChange = (color) => {
    if (color)
      setCustomColor({
        name: "primary_2",
        color: color,
      });
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();

    console.log("handle delete");
    setCustomColor({});
    if (selected == "primary_2") {
      handleClick("primary_1");
    }
  };

  const Picker = () => (
    <ColorPickerPopOver
      color={customColor?.color}
      callback={handleColorChange}
      clickNode={
        <GetFontAwesomeIcon
          icon={faPlus}
          color={"#3C2884"}
          iconStyles={{ fontSize: "1rem" }}
        />
      }
    />
  );

  const ColorBox = ({ data, children, handleClick }) => {
    const handleOnClick = (e, name) => {
      e.preventDefault();
      e.stopPropagation();
      handleClick(name);
    };
    return (
      <Box
        className="d-flex align-items-center justify-content-center"
        sx={{
          background: data.color,
          width: "50px",
          height: "50px",
          cursor: "pointer",
          borderRadius: "10px",
          border: "1px solid var(--palette-secondary_button_stroke)",
          position: "relative",
        }}
        onClick={(e) => {
          if (data.name) handleOnClick(e, data.name);
        }}
      >
        {children}
      </Box>
    );
  };

  return (
    <Box className="d-flex" sx={{ gap: 1 }}>
      {colors.map((tc, index) => {
        return (
          <ColorBox key={index} data={tc} handleClick={handleClick}>
            {tc.color && tc.name == selected ? (
              <GetFontAwesomeIcon
                icon={faCircleCheck}
                iconStyles={{
                  fontSize: "1rem",
                  marginRight: "0px",
                  color: selectedColor,
                }}
              />
            ) : null}
          </ColorBox>
        );
      })}
      {customColor.color ? (
        <ColorBox key={3} data={customColor} handleClick={handleClick}>
          <>
            <GetFontAwesomeIcon
              icon={faTrash}
              iconStyles={{
                position: "absolute",
                top: -3,
                right: -12,
                background: "var(--palette-secondary_button)",
                padding: "5px",
                border: "1px solid var(--palette-secondary_button_stroke)",
                borderRadius: "50%",
                fontSize: "0.75rem",
              }}
              onClick={handleDelete}
            />
            {customColor.color && customColor.name == selected ? (
              <GetFontAwesomeIcon
                icon={faCircleCheck}
                iconStyles={{
                  fontSize: "1rem",
                  marginRight: "0px",
                  color: selectedColor,
                }}
              />
            ) : null}
          </>
        </ColorBox>
      ) : (
        <ColorBox key={3} data={customColor}>
          <Picker />
        </ColorBox>
      )}
    </Box>
  );
};
